import React, { Component } from "react";
import { AppCard, LoadingDots, PageLayout, FormField } from "../../components/ui";
import { toastService } from "../../services/toastService";
import { utils } from "../../utils/utils";
import { Users } from "lucide-react";
import { CHANGE_STATUS_REASON, ClientAction, IssuingClient, IssuingClientStatus } from "../../models/IssuingClientsModel";
import ClientCardsComponent from "./ClientCards";
import ExtendedInputText from "../../components/ExtendedInputText";
import ClientEventsComponent from "./ClientEvents";
import { issuingApiService } from "../../services/api/Issuing/IssuingApiService";
import DocumentRequestComponent from "./ClientDocuments";
import { Dialog } from "primereact/dialog";
import { Label, Col, Card, CardHeader, CardBody, Row, FormGroup, Button } from "reactstrap";
import { userApiService } from "../../services/api/users/UsersApiService";
import history from "../../history";
import { Link } from "react-router-dom";
import { isUserProfileViewDetailsAllowed } from "../../authGuard";

interface ClientDetailsState {
    isLoading: boolean;
    client?: IssuingClient | null;
    individualId?: number;
    showStatusDialog: boolean;
    selectedAction?: string;
    selectedReason?: string;
    note?: string;
}

export default class ICardClientDetailsComponent extends Component<Record<string, never>, ClientDetailsState> {

    validationErrors: Map<string, boolean> = new Map<string, boolean>();

    constructor(props: Record<string, never>) {
        super(props);

        this.state = {
            isLoading: false,
            individualId: utils.getIdentifierFromPath(),
            client: null,
            showStatusDialog: false,
            selectedAction: undefined,
            selectedReason: undefined,
            note: ""

        };
    }
    updateClientDetails = async () => {
        const { client } = this.state;

        if (!client) return;

        this.setState({ isLoading: true });
        const res = await issuingApiService.updateClientDetails({ individualId: client.individualId! });
        if (res.success) {
            toastService.success("Client details updated successfully")

            this.getClientDetails(); // refresh
            this.setState({
                isLoading: false
            })
        } else {
            toastService.error(res.message || "Failed to update client details");
            this.setState({
                isLoading: false
            })
        }

    };
    getAvailableActions = (status?: IssuingClientStatus) => {
        switch (status) {

            case IssuingClientStatus.Active:
                return [
                    { label: "Block", next: ClientAction.Block },
                    { label: "Terminate", next: ClientAction.Terminate }
                ];

            case IssuingClientStatus.Suspended:
                return [
                    { label: "Activate", next: ClientAction.Activate },
                    { label: "Terminate", next: ClientAction.Terminate }
                ];

            case IssuingClientStatus.Pending:
                return [
                    { label: "Activate", next: IssuingClientStatus.Active },
                    { label: "Reject", next: IssuingClientStatus.Rejected },
                ];

            case IssuingClientStatus.Blocked:
                return [
                    { label: "Activate", next: ClientAction.Activate },
                    { label: "Terminate", next: ClientAction.Terminate }
                ];

            case IssuingClientStatus.BlockedFraudSuspected:
                return [
                    { label: "Activate", next: ClientAction.Activate },
                    { label: "Terminate", next: ClientAction.Terminate }
                ];

            case IssuingClientStatus.Closed:
                return [];

            default:
                return [];
        }
    };

    renderStatusActionsSection = () => {
        const status = this.state.client?.clientStatus;
        if (!status) return null;

        const actions = this.getAvailableActions(status);
        if (!actions.length) return null;

        const destructiveStatuses = [
            ClientAction.Block,
            ClientAction.Terminate
        ];

        return (
            <div className="p-3 mt-2 border rounded bg-light">
                <Label className="text-muted mb-2">Available Actions</Label>

                <div className="d-flex flex-wrap gap-2">
                    {actions.map((action) => {
                        const isDanger = destructiveStatuses.includes(action.next);

                        return (
                            <Button
                                key={action.label}
                                size="sm"
                                color={isDanger ? "danger" : "primary"}
                                outline={!isDanger}
                                onClick={() =>
                                    this.setState({
                                        showStatusDialog: true,
                                        selectedAction: action.next,
                                        selectedReason: undefined,
                                        note: ""
                                    })
                                }
                            >
                                {action.label}
                            </Button>
                        );
                    })}
                </div>
            </div>
        );
    };

    async getClientDetails() {
        const { individualId } = this.state;
        if (!individualId) return;

        this.setState({ isLoading: true });

        try {
            const [res, individualResponse] = await Promise.all([
                issuingApiService.getClientDetails(individualId),
                userApiService.get(individualId)
            ]);

            if (res?.success && individualResponse?.success) {
                const { firstName = '', lastName = '' } = individualResponse.data || {};

                this.setState({
                    client: {
                        ...res.data,
                        name: `${firstName} ${lastName}`.trim()
                    }
                });
            } else {
                console.error('API error', { res, individualResponse });
            }
        } catch (error) {
            console.error('getClientDetails failed:', error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    componentDidMount(): void {
        this.getClientDetails();
    }

    updateValue = (fieldName: string, value: string) => {
        const merchantDetails = this.state.client as any;

        merchantDetails[fieldName] = value;

        this.setState({ client: merchantDetails });
    };

    submitStatusChange = async () => {
        const { client, selectedAction, selectedReason, note } = this.state;

        if (!client || !selectedAction || !selectedReason || !note) return;

        this.setState({ isLoading: true });

        const res = await issuingApiService.updateClientStatus({
            individualId: client.individualId,
            statusAction: selectedAction,
            changeStatusReason: selectedReason,
            note: note || null
        });

        if (res.success) {
            toastService.success("Status updated successfully");
            this.setState({ showStatusDialog: false });
            this.getClientDetails();
        } else {
            toastService.error(res.message)

        }

        this.setState({ isLoading: false });
    };

    renderClientBody = () => {
        if (this.state.isLoading || !this.state.client) {
            return (
                <Col md={12} lg={12}>
                    <Card style={{ minHeight: "500px" }} className="align-center">
                        {/* <ReactLoader type="ball-pulse" active /> */}
                    </Card>
                </Col>
            );
        }

        return (
            <Col md={12} lg={12}>
                <Card className="mb-3 border rounded-lg overflow-hidden">
                    <CardHeader className="card-header-tab">
                        <div className="card-header-title font-size-lg text-capitalize font-weight-normal">
                            <i className="header-icon lnr-laptop-phone mr-3 text-muted opacity-6"> </i>
                            Client Details
                        </div>
                    </CardHeader>
                    <CardBody>
                        <Row>

                            <Col md={12} lg={12} sm="12">
                                <Row>

                                    <Col md={2}>
                                        <FormGroup>
                                            <Label>Individual-ID</Label>

                                            <div className="form-control !h-auto !py-[11px]">
                                                {isUserProfileViewDetailsAllowed() ? (
                                                    <Link
                                                        to={`/user-profile/${this.state.client?.individualId}`}
                                                        style={{
                                                            textDecoration: "none",
                                                            color: "#007bff",
                                                            cursor: "pointer"
                                                        }}
                                                    >
                                                        {this.state.client?.individualId}
                                                    </Link>
                                                ) : (
                                                    this.state.client?.individualId
                                                )}
                                            </div>
                                        </FormGroup>
                                    </Col>

                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Client Name</Label>
                                            <ExtendedInputText
                                                type="id"
                                                value={this.state.client?.name}
                                                onChange={(value) => this.updateValue("name", value)}
                                                readOnly={true}
                                                name="id"
                                                // required={true}
                                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("name", value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>CBS-ID</Label>
                                            <ExtendedInputText
                                                name="flexCubeCbsId"
                                                value={this.state.client?.flexCubeCbsId}
                                                // required={true}
                                                readOnly={true}
                                                onChange={(value) => this.updateValue("flexCubeCbsId", value)}
                                                onValidStatusChanged={(value: boolean) => this.validationErrors.set("flexCubeCbsId", value)}
                                            />
                                        </FormGroup>
                                    </Col>



                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label className="font-weight-bold">Current Status</Label>

                                            <div className="d-flex align-items-center mb-3">
                                                <span
                                                    className={`badge badge-pill px-3 py-2 mr-3 
                                                    ${utils.getClientStatusBadgeClass(this.state.client?.clientStatus)}`}
                                                    style={{ fontSize: "0.95rem" }}
                                                >
                                                    {IssuingClientStatus[
                                                        this.state.client?.clientStatus as keyof typeof IssuingClientStatus
                                                    ]}
                                                </span>

                                                <div style={{ padding: "15px" }}>
                                                    <Button
                                                        type="button"
                                                        disabled={Object.values(this.validationErrors).filter((f) => f == false).length > 0}
                                                        label="Update"
                                                        icon="pi pi-check"
                                                        className="p-button"
                                                        color={"primary"}
                                                        onClick={() => this.updateClientDetails()}
                                                    >Send to iCard</Button>
                                                </div>


                                            </div>

                                            {this.renderStatusActionsSection()}
                                        </FormGroup>
                                    </Col>
                                </Row>


                            </Col>

                        </Row>

                        <Dialog
                            header={`Update Status - ${this.state.selectedAction}`}
                            visible={this.state.showStatusDialog}
                            style={{ width: "400px" }}
                            onHide={() => this.setState({ showStatusDialog: false })}
                        >
                            <div className="p-fluid">

                                {/* Reason */}
                                <FormGroup>
                                    <Label>Reason *</Label>
                                    <select
                                        className="form-control"
                                        value={this.state.selectedReason || ""}
                                        onChange={(e) =>
                                            this.setState({ selectedReason: e.target.value })
                                        }
                                    >
                                        <option value="">Select</option>
                                        {Object.values(CHANGE_STATUS_REASON).map((r: any) => (
                                            <option key={r} value={r}>
                                                {r}
                                            </option>
                                        ))}
                                    </select>
                                </FormGroup>

                                {/* Note */}
                                <FormGroup>
                                    <Label>Note *</Label>
                                    <ExtendedInputText
                                        value={this.state.note}
                                        onChange={(val) => this.setState({ note: val })}
                                        name="note"
                                    />
                                </FormGroup>

                                {/* Actions */}
                                <div className="flex justify-content-end gap-2 mt-3">
                                    <Button
                                        label="Cancel"

                                        className="p-button-text"
                                        onClick={() => this.setState({ showStatusDialog: false })}
                                    >Cancel</Button>

                                    <Button
                                        label="Confirm"
                                        icon="pi pi-check"
                                        color={"primary"}
                                        disabled={!this.state.selectedReason || !this.state.note}
                                        onClick={this.submitStatusChange}
                                    >Confirm</Button>
                                </div>
                            </div>
                        </Dialog>
                    </CardBody>
                </Card>
            </Col>
        );
    };
    render() {
        const { client, isLoading, individualId } = this.state;

        return (
            <PageLayout heading="Client Details" icon={Users}>
                <div className="flex flex-col gap-4">

                    {/* ── Client Info Card ── */}
                    <AppCard>
                        <AppCard.Header title="Client Details" />
                        <AppCard.Body>
                            {isLoading || !client ? (
                                <div className="flex items-center justify-center min-h-[200px]">
                                    <LoadingDots />
                                </div>
                            ) : (
                                <>
                                    {this.renderClientBody()}
                                </>
                            )}
                        </AppCard.Body>
                    </AppCard>

                    {/* ── Sub-sections ── */}
                    <DocumentRequestComponent IndividualId={individualId} />
                    <ClientCardsComponent IndividualId={individualId} />
                    <ClientEventsComponent IndividualId={individualId} />

                </div>
            </PageLayout>
        );
    }
}
